const TOKEN_KEY = "godo_token";
const PROFILE_KEY = "godo_profile";

export function getApiBaseUrl(): string {
  const base = import.meta.env.VITE_API_BASE_URL?.trim();
  return base && base.length > 0 ? base.replace(/\/$/, "") : "/api";
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getStoredProfile<T>(): T | null {
  const raw = localStorage.getItem(PROFILE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function setStoredProfile(profile: unknown): void {
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
}

export function clearStoredProfile(): void {
  localStorage.removeItem(PROFILE_KEY);
}

export function clearSession(): void {
  clearStoredToken();
  clearStoredProfile();
}

let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Skip Authorization header (login). */
  skipAuth?: boolean;
}

function buildUrl(path: string): string {
  const base = getApiBaseUrl();
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${base}${normalized}`;
}

/**
 * Global fetch wrapper — attaches JWT, parses JSON, handles 401 → login redirect.
 */
import { ApiClientError } from "./types";

const DEFAULT_TIMEOUT_MS = 25_000;

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { body, skipAuth, headers, ...rest } = options;

  const reqHeaders = new Headers(headers);
  if (body !== undefined && !reqHeaders.has("Content-Type")) {
    reqHeaders.set("Content-Type", "application/json");
  }

  if (!skipAuth) {
    const token = getStoredToken();
    if (token) {
      reqHeaders.set("Authorization", `Bearer ${token}`);
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      ...rest,
      headers: reqHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new ApiClientError(
        "انتهت مهلة الاتصال بالسيرفر. تأكد أن wrangler dev يعمل على المنفذ 8787.",
        0,
        -1,
        "timeout",
      );
    }
    throw new ApiClientError(
      "تعذر الاتصال بالسيرفر. تأكد أن wrangler dev يعمل.",
      0,
      -1,
      "network_error",
    );
  } finally {
    clearTimeout(timeoutId);
  }

  let payload: unknown = null;
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    payload = await response.json();
  } else if (!response.ok) {
    throw new ApiClientError(
      response.statusText || "Request failed",
      response.status,
      -1,
    );
  }

  if (response.status === 401 && !skipAuth) {
    clearSession();
    unauthorizedHandler?.();
    const errBody = payload as { error?: { message?: string; code?: number; reason?: string } } | null;
    throw new ApiClientError(
      errBody?.error?.message ?? "Session expired",
      401,
      errBody?.error?.code ?? 401,
      errBody?.error?.reason ?? "unauthorized",
    );
  }

  if (!response.ok) {
    const errBody = payload as {
      error?: {
        message?: string;
        code?: number;
        reason?: string;
        paywall?: unknown;
      };
    } | null;
    throw new ApiClientError(
      errBody?.error?.message ?? "Request failed",
      response.status,
      errBody?.error?.code ?? response.status,
      errBody?.error?.reason,
      errBody?.error?.paywall,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "GET" }),

  post: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "POST", body }),

  patch: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "PATCH", body }),

  put: <T>(path: string, body?: unknown, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "PUT", body }),

  delete: <T>(path: string, init?: RequestOptions) =>
    apiRequest<T>(path, { ...init, method: "DELETE" }),
};
